
import React, { useState, useRef, useEffect } from 'react';
import { getCleaningAdvice } from '../services/gemini';
import { ChatMessage, Product } from '../types';

interface AIAssistantProps {
  products: Product[];
  isOpen: boolean;
  onToggle: (open: boolean) => void;
}

const AIAssistant: React.FC<AIAssistantProps> = ({ products, isOpen, onToggle }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: 'assistant', content: "Hi! I'm Lynsey, your homecare expert. Ready to make your home sparkle? Ask me anything about cleaning or my special range, love!" }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // High-resolution photo provided by the user
  const LYNSEY_PHOTO = "https://images.squarespace-cdn.com/content/6962244a930d3931ee04d503/d5e209d2-8a40-498c-805b-924c27c3cad5/Screenshot+2026-01-14+at+17.21.19.png?content-type=image%2Fpng";

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading, isOpen]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const advice = await getCleaningAdvice(userMessage, messages, products);
      setMessages(prev => [...prev, { role: 'assistant', content: advice }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: "Oh dear, I seem to have lost my connection. Could you say that again, love?" }]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="absolute top-full left-0 right-0 sm:left-auto sm:right-0 mt-2 z-[9999] flex flex-col items-center sm:items-end pointer-events-auto">
      <div className="w-[calc(100vw-2rem)] sm:w-[350px] md:w-[400px] h-[500px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-slideUp">
        <div className="p-5 bg-[#F1B1BA] text-[#5C3E43] flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full border-2 border-white shadow-sm flex items-center justify-center bg-white overflow-hidden">
                <img 
                  src={LYNSEY_PHOTO} 
                  alt="Lynsey Crombie" 
                  className="w-full h-full object-cover object-center"
                  style={{ imageRendering: '-webkit-optimize-contrast' }}
                />
              </div>
              <div className="absolute -bottom-1 -right-1 bg-green-400 w-3 h-3 rounded-full border-2 border-white animate-pulse"></div>
            </div>
            <div>
              <h3 className="font-black text-sm uppercase tracking-widest">Lynsey Answers</h3>
              <div className="flex items-center space-x-1">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                <p className="text-[10px] text-[#5C3E43]/70 font-black uppercase tracking-tighter">Live Expert Advice</p>
              </div>
            </div>
          </div>
          <button onClick={() => onToggle(false)} className="hover:bg-white/20 p-2 rounded-md transition-colors">
            <i className="fas fa-times text-xs"></i>
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 p-5 overflow-y-auto space-y-4 custom-scrollbar bg-white">
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'assistant' && (
                <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 mt-1 mr-2 border border-slate-100 bg-white flex items-center justify-center">
                  <img 
                    src={LYNSEY_PHOTO} 
                    alt="Lynsey Crombie" 
                    className="w-full h-full object-cover object-center"
                    style={{ imageRendering: '-webkit-optimize-contrast' }}
                  />
                </div>
              )}
              <div className={`max-w-[85%] p-4 rounded-xl text-xs leading-relaxed ${
                msg.role === 'user' 
                  ? 'bg-[#F1B1BA] text-[#5C3E43] rounded-br-none shadow-md shadow-pink-900/10' 
                  : 'bg-slate-50 border border-slate-100 text-slate-800 rounded-bl-none shadow-sm'
              }`}>
                {msg.content}
              </div>
            </div>
          ))}
          {isLoading && (
            <div className="flex justify-start items-center">
               <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 mr-2 border border-slate-100 bg-white flex items-center justify-center">
                  <img 
                    src={LYNSEY_PHOTO} 
                    alt="Lynsey Crombie" 
                    className="w-full h-full object-cover object-center"
                    style={{ imageRendering: '-webkit-optimize-contrast' }}
                  />
                </div>
              <div className="bg-slate-50 border border-slate-100 p-3 rounded-xl rounded-bl-none flex space-x-1">
                <div className="w-1.5 h-1.5 bg-[#F1B1BA] rounded-full animate-bounce"></div>
                <div className="w-1.5 h-1.5 bg-[#F1B1BA] rounded-full animate-bounce delay-100"></div>
                <div className="w-1.5 h-1.5 bg-[#F1B1BA] rounded-full animate-bounce delay-200"></div>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 bg-slate-50 border-t border-slate-100">
          <div className="relative">
            <input 
              type="text" 
              placeholder="Ask Lynsey for cleaning tips..." 
              className="w-full pl-4 pr-12 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#F1B1BA] focus:border-transparent transition-all text-xs font-semibold text-black shadow-inner"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            />
            <button 
              onClick={handleSendMessage}
              disabled={isLoading}
              className={`absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg transition-all ${
                inputValue.trim() ? 'bg-[#5C3E43] text-white shadow-md' : 'bg-slate-100 text-slate-300'
              }`}
            >
              <i className="fas fa-paper-plane text-[10px]"></i>
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #F1B1BA;
        }
      `}</style>
    </div>
  );
};

export default AIAssistant;
